import { batch } from 'react-redux';
import { initialize } from 'redux-form';
import { useState } from 'react';
import { DataTestId, TextKey } from '@mews-commander/enums';
import { CommanderThunkAction } from '@mews-commander/types';
import { LocalizedText, lt } from '@mews/framework';
import { Button, Dialog, Stack } from '@optimus-web/core';
import {
    localizeCurrencyValue,
    throttleLeading,
    useCommanderSelector,
    useCommanderThunkDispatch,
} from '@mews-commander/utils';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { createCardLabel, getBillOwnerId } from '@mews-commander/legacy-entities/src/ducks';
import { useFormValueSelector } from '@mews-commander/legacy-form';
import { isDialogOpen } from '../../../../dialog/selectors';
import { closeDialog, openDialog } from '../../../../dialog/reducer';
import { getAmountDue, getPaymentCard } from '../../../selectors';
import PaymentCardIcon from '../../../../../components/commander-ui/PaymentCardIcon';
import { handlePaymentCardClick } from '../../../actions/payments';
import { useInvalidatePaymentsTableRecordsQueries } from '../../../../payment/queries';

export const CHARGE_AND_CLOSE_CONFIRMATION = 'chargeAndCloseConfirmation';

const ChargeAndCloseConfirmationDialog = () => {
    const [loading, setLoading] = useState(false);
    const dispatch = useCommanderThunkDispatch();
    const active = useCommanderSelector((state) => isDialogOpen(state, { name: CHARGE_AND_CLOSE_CONFIRMATION }));
    const cardId = useFormValueSelector(CHARGE_AND_CLOSE_CONFIRMATION, 'cardId');
    const billId = useFormValueSelector(CHARGE_AND_CLOSE_CONFIRMATION, 'billId');
    const scope = useFormValueSelector(CHARGE_AND_CLOSE_CONFIRMATION, 'scope');
    const paymentCard = useCommanderSelector((state) => getPaymentCard(state, { id: cardId }));
    const enterpriseId = useCurrentEnterpriseId();
    const amount = useCommanderSelector((state) => getAmountDue(state, { billIds: [billId], enterpriseId }));
    const accountId = useCommanderSelector((state) => getBillOwnerId(state, { id: billId }));
    const invalidatePaymentsTableRecordsQueries = useInvalidatePaymentsTableRecordsQueries();

    if (!active) return null;
    if ((!paymentCard || !amount) && active) {
        throw new Error(`Payment card with id ${cardId} not found or amount not calculated.`);
    }

    const cardName = createCardLabel(paymentCard);
    const amountString = localizeCurrencyValue({ value: amount.grossValue, currency: amount.currency });

    const close = () => dispatch(closeDialog(CHARGE_AND_CLOSE_CONFIRMATION));

    const onSubmit = throttleLeading(async () => {
        try {
            setLoading(true);
            await dispatch(handlePaymentCardClick({ creditCardId: cardId, billId, scope }));
            if (accountId) invalidatePaymentsTableRecordsQueries({ accountId });
        } finally {
            setLoading(false);
            close();
        }
    });

    return (
        <Dialog
            loading={loading}
            footer={{
                primary: (
                    <>
                        <Button onClick={close} variant="tertiary" size="medium" type="submit">
                            {lt('Cancel')}
                        </Button>
                        <Button
                            onClick={onSubmit}
                            variant="primary"
                            size="medium"
                            data-test-id={DataTestId.ConfirmChargeAndCloseButton}
                            type="submit"
                        >
                            {lt('Confirm')}
                        </Button>
                    </>
                ),
            }}
            title={<LocalizedText textKey={TextKey.ChargeCardAndCloseBill} />}
            onClose={close}
            active={active}
            size="small"
        >
            <Stack spacing="100">
                <PaymentCardIcon type={paymentCard.type} width={24} />
                <LocalizedText
                    textKey={TextKey.CardWillBeChargedAmount}
                    substitutes={{
                        amount: amountString,
                        card: cardName,
                    }}
                />
            </Stack>
        </Dialog>
    );
};

interface OpenChargeAndCloseConfirmationDialogProps {
    cardId: string;
    billId: string;
    scope: string;
}

export const openChargeAndCloseConfirmationDialog =
    ({ cardId, billId, scope }: OpenChargeAndCloseConfirmationDialogProps): CommanderThunkAction =>
    (dispatch) =>
        batch(() => {
            dispatch(openDialog(CHARGE_AND_CLOSE_CONFIRMATION));
            dispatch(
                initialize(CHARGE_AND_CLOSE_CONFIRMATION, {
                    cardId,
                    billId,
                    scope,
                })
            );
        });

export default ChargeAndCloseConfirmationDialog;
